import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useTest } from '../context/TestContext';
import { 
  FaCamera, 
  FaMicrophone, 
  FaPlay, 
  FaStop,
  FaArrowLeft,
  FaArrowRight,
  FaRedo,
  FaCheck,
  FaVolumeUp
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import CameraBox from './CameraBox';

const TestContainer = styled.div`
  min-height: 100vh;
  padding: 20px;
  display: flex;
  flex-direction: column;
  color: white;
  position: relative;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const ProgressInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const ProgressText = styled.div`
  font-size: 18px;
  font-weight: 600;
`;

const ProgressBar = styled.div`
  width: 200px;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  overflow: hidden;
`;

const ProgressFill = styled(motion.div)`
  height: 100%;
  background: linear-gradient(90deg, #10b981, #34d399);
  border-radius: 4px;
`;

const Timer = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #10b981;
`;

const MainContent = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  flex: 1;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

const CameraSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const CameraContainer = styled.div`
  position: relative;
  width: 100%;
  height: 400px;
  background: linear-gradient(135deg, #1f2937, #374151);
  border-radius: 20px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CameraControls = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
`;

const ControlButton = styled(motion.button)`
  width: 60px;
  height: 60px;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  ${props => props.variant === 'record' && `
    background: linear-gradient(135deg, #ef4444, #dc2626);
    color: white;
    box-shadow: 0 4px 15px rgba(239, 68, 68, 0.3);
    
    &:hover {
      transform: scale(1.1);
      box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
    }
  `}
  
  ${props => props.variant === 'stop' && `
    background: linear-gradient(135deg, #6b7280, #4b5563);
    color: white;
    
    &:hover {
      transform: scale(1.1);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.2);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: scale(1.1);
    }
  `}
`;

const InstructionsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InstructionCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(20px);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  padding: 30px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

const InstructionTitle = styled.h3`
  font-size: 24px;
  font-weight: 600;
  margin-bottom: 15px;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const InstructionText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  margin-bottom: 20px;
  opacity: 0.9;
`;

const InstructionDuration = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 15px;
  background: rgba(16, 185, 129, 0.2);
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
`;

const NavigationButtons = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  margin-top: auto;
`;

const NavButton = styled(motion.button)`
  padding: 15px 30px;
  border: none;
  border-radius: 12px;
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 10px;
  
  ${props => props.variant === 'primary' && `
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: white;
    box-shadow: 0 4px 15px rgba(37, 99, 235, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(37, 99, 235, 0.4);
    }
  `}
  
  ${props => props.variant === 'secondary' && `
    background: rgba(255, 255, 255, 0.1);
    color: white;
    border: 1px solid rgba(255, 255, 255, 0.2);
    backdrop-filter: blur(10px);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: translateY(-2px);
    }
  `}
  
  ${props => props.disabled && `
    opacity: 0.5;
    cursor: not-allowed;
    transform: none !important;
  `}
`;

const TestScreen = ({ onComplete }) => {
  const { 
    testItems, 
    currentTestItem, 
    isRecording, 
    actions 
  } = useTest();
  
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordedVideo, setRecordedVideo] = useState(null);
  const intervalRef = useRef(null);

  const currentItem = testItems[currentTestItem];

  const handleStopRecording = useCallback(() => {
    actions.setRecording(false);
    setIsPlaying(false);
    clearInterval(intervalRef.current);
    
    // Simuler l'enregistrement d'une vidéo
    const videoUrl = `data:video/mp4;base64,${btoa('simulated-video-data')}`;
    setRecordedVideo(videoUrl);
    actions.updateTestItem(currentTestItem, { 
      videoRecorded: true, 
      videoUrl 
    });
    
    toast.success('Enregistrement terminé');
  }, [actions, currentTestItem]);

  useEffect(() => {
    if (isRecording && isPlaying) {
      intervalRef.current = setInterval(() => {
        setCurrentTime(prev => {
          if (prev >= currentItem.duration) {
            handleStopRecording();
            return prev;
          }
          return prev + 1;
        });
      }, 1000);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isRecording, isPlaying, currentItem.duration, handleStopRecording]);

  const handleStartRecording = () => {
    actions.setRecording(true);
    setIsPlaying(true);
    setCurrentTime(0);
    toast.success('Enregistrement démarré');
  };

  const handleReplay = () => {
    if (recordedVideo) {
      toast.success('Lecture de la vidéo');
    }
  };

  const handleNext = () => {
    if (currentTestItem < testItems.length - 1) {
      actions.setCurrentTestItem(currentTestItem + 1);
      setCurrentTime(0);
      setIsPlaying(false);
      setRecordedVideo(null);
    } else {
      actions.setTestComplete(true);
      onComplete();
    }
  };

  const handlePrevious = () => {
    if (currentTestItem > 0) {
      actions.setCurrentTestItem(currentTestItem - 1);
      setCurrentTime(0);
      setIsPlaying(false);
      setRecordedVideo(null);
    }
  };

  const progress = ((currentTestItem + 1) / testItems.length) * 100;

  return (
    <TestContainer>
      <Header>
        <ProgressInfo>
          <ProgressText>
            Test {currentTestItem + 1} sur {testItems.length}
          </ProgressText>
          <ProgressBar>
            <ProgressFill
              initial={{ scaleX: 0 }}
              animate={{ scaleX: progress / 100 }}
              transition={{ duration: 0.5 }}
            />
          </ProgressBar>
        </ProgressInfo>
        <Timer>
          {Math.floor(currentTime / 60)}:{(currentTime % 60).toString().padStart(2, '0')}
        </Timer>
      </Header>

      <MainContent>
        <CameraSection>
          <CameraContainer>
            <CameraBox />
          </CameraContainer>

          <CameraControls>
            {!isRecording ? (
              <ControlButton
                variant="record"
                onClick={handleStartRecording}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaPlay />
              </ControlButton>
            ) : (
              <ControlButton
                variant="stop"
                onClick={handleStopRecording}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaStop />
              </ControlButton>
            )}
            
            {recordedVideo && (
              <ControlButton
                variant="secondary"
                onClick={handleReplay}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <FaRedo />
              </ControlButton>
            )}
          </CameraControls>
        </CameraSection>

        <InstructionsSection>
          <InstructionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <InstructionTitle>
              <FaCamera />
              {currentItem.title}
            </InstructionTitle>
            <InstructionText>{currentItem.description}</InstructionText>
            <InstructionDuration>
              <FaVolumeUp />
              Durée: {currentItem.duration} secondes
            </InstructionDuration>
          </InstructionCard>

          <InstructionCard
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <InstructionTitle>
              <FaMicrophone />
              Instruction pour le patient
            </InstructionTitle>
            <InstructionText style={{ fontSize: '18px', fontWeight: '500' }}>
              "{currentItem.instruction}"
            </InstructionText>
          </InstructionCard>
        </InstructionsSection>
      </MainContent>

      <NavigationButtons>
        <NavButton
          variant="secondary"
          onClick={handlePrevious}
          disabled={currentTestItem === 0}
          whileHover={{ scale: currentTestItem > 0 ? 1.02 : 1 }}
          whileTap={{ scale: currentTestItem > 0 ? 0.98 : 1 }}
        >
          <FaArrowLeft />
          Précédent
        </NavButton>

        <NavButton
          variant="primary"
          onClick={handleNext}
          disabled={!currentItem.videoRecorded}
          whileHover={{ scale: currentItem.videoRecorded ? 1.02 : 1 }}
          whileTap={{ scale: currentItem.videoRecorded ? 0.98 : 1 }}
        >
          {currentTestItem === testItems.length - 1 ? (
            <>
              <FaCheck />
              Terminer le test
            </>
          ) : (
            <>
              Suivant
              <FaArrowRight />
            </>
          )}
        </NavButton>
      </NavigationButtons>
    </TestContainer>
  );
};

export default TestScreen; 